import React, { useState, useEffect, useRef, useCallback } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import AppTheme from '../shared-theme/AppTheme';
import AppAppBar from './components/AppAppBar';
import MainContent from './components/MainContent';
import TeeTimeContent from './components/TeeTimeContent';
import ScoresContent from './components/ScoresContent';
import BattleForMuniContent from './components/BattleForMuniContent';
import BattleForEmeraldContent from './components/BattleForEmeraldContent';
import Latest from './components/Latest';
import Footer from './components/Footer';
import { SubmitTileForm } from './components/SubmitTileForm';
import { SubmitScoreForm } from './components/SubmitScoreForm';
import {Dialog, Typography, Button, Box, IconButton} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { EdcProvider, useEdc } from './edc/EdcContext';
import useEdcSecret from './edc/useEdcSecret';
import useEdcTapSecret from './edc/useEdcTapSecret';
import EdcMeltOverlay from './edc/EdcMeltOverlay';
import EdcBubbles from './edc/EdcBubbles';
import BattleForEdcContent from './edc/BattleForEdcContent';
import './edc/edc.css';

const parByHoleMuni = [4, 4, 3, 5, 3, 4, 4, 5, 4, 4, 3, 4, 3, 5, 4, 4, 5, 4];

const parByHoleEm = [3,4,3,3,3,3,4,3,3,3,3,3,3,3,3,3,3,3];

function BlogInner(props) {
const theme = useTheme();
const { edcMode, toggleEdc, bubblesEnabled, toggleBubbles } = useEdc();
useEdcSecret(toggleEdc, { enabled: true });
// Mobile-only secondary trigger: tap rule circles 3 -> 2 -> 1 inside 3s.
// We disable it once we're already in EDC mode so re-tapping doesn't melt
// back out.
const handleRuleTap = useEdcTapSecret(toggleEdc, { enabled: !edcMode });
console.log('PRIMARY:', theme.palette.primary.main);
console.log('INFO:', theme.palette.info.main);
  const [view, setView] = useState(' ');
  const viewRef = useRef(view);
  const selectView = useCallback((nextView) => {
    if (viewRef.current !== ' ' && viewRef.current !== nextView) {
      localStorage.removeItem('savedBattleKey');
      localStorage.removeItem('savedBattleKeyScope');
    }
    viewRef.current = nextView;
    setView(nextView);
  }, []);
  const [showTileForm, setShowTileForm] = useState(false);
  const [showScoreForm, setShowScoreForm] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showEmailThankYou, setShowEmailThankYou] = useState(false);
  const [territories, setTerritories] = useState([]);
  const [muniHoles, setMuniHoles] = useState([]);
  const [muniTimeline, setMuniTimeline] = useState({});
  const [emTimeline, setEmTimeline] = useState({});
  // EDC RSVPs ("I'm Going" claims) — Contentful content type `edcSetClaim`.
  // Reshaped on fetch into the same prop shape BattleForEdcContent expects
  // (`territories`) so the existing rendering code keeps working.
  const [edcGoing, setEdcGoing] = useState([]);
  const [showBottomNav, setShowBottomNav] = useState(false);
  // When a user pops a bubble or hits "I'm Going" on a scheduled set,
  // we stash the set here so the SubmitScoreForm dialog can prefill itself.
  const [prefillSet, setPrefillSet] = useState(null);

  const openScoreFormWith = (set) => {
    setPrefillSet(set || null);
    setShowScoreForm(true);
  };

  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY < lastScrollY.current) {
        setShowBottomNav(true); // scrolling up
      } else {
        setShowBottomNav(false); // scrolling down
      }
      lastScrollY.current = currentY;
    };
  
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (edcMode) {
      selectView('battle-for-edc');
      return;
    }
    // Only honor `savedCourse` for the real Golf Boys battles. If a prior
    // EDC RSVP persisted `savedCourse = 'edc'` while EDC mode was on,
    // setting view to 'battle-for-edc' here (with edcMode now false) leaves
    // the main body empty because the !edcMode JSX branch below only renders
    // 'battle-for-muni' / 'battle-for-em'. Fall back to muni in that case.
    const savedCourse = localStorage.getItem('savedCourse');
    const validCourses = ['muni', 'em'];
    if (savedCourse && validCourses.includes(savedCourse)) {
      selectView(`battle-for-${savedCourse}`);
    } else {
      selectView('battle-for-muni');
    }
  }, [edcMode, selectView]);

  const fetchTerritoryContent = async () => {
    const SPACE_ID = 'zw84cuagwed8';
    const ACCESS_TOKEN = 'V5ofoTzcdDzxrgiKxseGpE-klKXNizC-B--eXor9aI0';
    const url = `https://cdn.contentful.com/spaces/${SPACE_ID}/environments/master/entries?access_token=${ACCESS_TOKEN}&content_type=territorySubmission&include=3`;
    const resp = await fetch(url);
    const data = await resp.json();
  
    const rawEntries = data.items
      .filter(entry => entry.fields.approved)
      .map(entry => ({
        ...entry.fields,
        createdAt: entry.sys.createdAt,
      }));

      
      const fullTimelineMap = {};

      for (const entry of rawEntries) {
        const t = entry.territory;
        if (!fullTimelineMap[t]) fullTimelineMap[t] = [];
        fullTimelineMap[t].push(entry);
      }

      for (const t in fullTimelineMap) {
        fullTimelineMap[t].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      }

      setEmTimeline(fullTimelineMap); 
  
    // count how many times each territory appears
    const territoryCount = {};
    for (const entry of rawEntries) {
      territoryCount[entry.territory] = (territoryCount[entry.territory] || 0) + 1;
    }
  
    // get latest submission per territory
    const territoryMap = {};
    for (const entry of rawEntries) {
      const key = entry.territory;
      if (
        !territoryMap[key] ||
        new Date(entry.createdAt) > new Date(territoryMap[key].createdAt)
      ) {
        territoryMap[key] = entry;
      }
    }
  
    const finalEntries = Object.values(territoryMap).map(entry => {
      const par = parByHoleEm[Number(entry.territory) - 1];
      const isBlown = Number(entry.score) >= par * 2;

      return {
        ...entry,
        isLatest: true,
        isDuplicate: territoryCount[entry.territory] > 1,
        isBlown,
      };
    });

    console.log(rawEntries, finalEntries);
    setTerritories(finalEntries);
  };


  const fetchMuniContent = async () => {
    const SPACE_ID = 'zw84cuagwed8';
    const ACCESS_TOKEN = 'V5ofoTzcdDzxrgiKxseGpE-klKXNizC-B--eXor9aI0';
    const url = `https://cdn.contentful.com/spaces/${SPACE_ID}/environments/master/entries?access_token=${ACCESS_TOKEN}&content_type=oceansideClaim&include=3`;
    const resp = await fetch(url);
    const data = await resp.json();

    const rawEntries = data.items
      .filter(entry => entry.fields.approved)
      .map(entry => ({
        ...entry.fields,
        createdAt: entry.sys.createdAt,
      }));

      const fullTimelineMap = {};

      for (const entry of rawEntries) {
        const t = entry.territory;
        if (!fullTimelineMap[t]) fullTimelineMap[t] = [];
        fullTimelineMap[t].push(entry);
      }

      for (const t in fullTimelineMap) {
        fullTimelineMap[t].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      }

      setMuniTimeline(fullTimelineMap); 
  
    // count how many times each territory appears
    const territoryCount = {};
    for (const entry of rawEntries) {
      territoryCount[entry.territory] = (territoryCount[entry.territory] || 0) + 1;
    }
  
    // get latest submission per territory
    const territoryMap = {};
    for (const entry of rawEntries) {
      const key = entry.territory;
      if (
        !territoryMap[key] ||
        new Date(entry.createdAt) > new Date(territoryMap[key].createdAt)
      ) {
        territoryMap[key] = entry;
      }
    }
  
    const finalEntries = Object.values(territoryMap).map(entry => {
      const par = parByHoleMuni[Number(entry.territory) - 1];
      const isBlown = Number(entry.score) >= par * 2;

      return {
        ...entry,
        isLatest: true,
        isDuplicate: territoryCount[entry.territory] > 1,
        isBlown,
      };
    });
  
    console.log(rawEntries, finalEntries);
    setMuniHoles(finalEntries);
  };

  // Pull EDC RSVPs out of Contentful and flatten them to the shape
  // BattleForEdcContent's `territories` prop expects: `battleKey` is the
  // user's crew tag, `territory` is the set id (so the existing
  // `setMatchesTerritory` helper keeps working).
  const fetchEdcContent = async () => {
    const SPACE_ID = 'zw84cuagwed8';
    const ACCESS_TOKEN = 'V5ofoTzcdDzxrgiKxseGpE-klKXNizC-B--eXor9aI0';
    const url = `https://cdn.contentful.com/spaces/${SPACE_ID}/environments/master/entries?access_token=${ACCESS_TOKEN}&content_type=edcSetClaim&include=3`;
    try {
      const resp = await fetch(url);
      if (!resp.ok) {
        // Content type may not exist yet in Contentful — fail soft.
        setEdcGoing([]);
        return;
      }
      const data = await resp.json();
      const items = Array.isArray(data.items) ? data.items : [];
      const reshaped = items
        .filter((entry) => entry.fields && entry.fields.approved !== false)
        .map((entry) => ({
          territory:  entry.fields.setId,
          battleKey:  entry.fields.userTag,
          score:      entry.fields.hype,
          stage:      entry.fields.stage,
          artist:     entry.fields.artist,
          day:        entry.fields.day,
          startTime:  entry.fields.startTime,
          note:       entry.fields.note,
          createdAt:  entry.sys.createdAt,
          isLatest:   true,
          isBlown:    false,
        }));
      setEdcGoing(reshaped);
    } catch (err) {
      console.warn('fetchEdcContent failed', err);
      setEdcGoing([]);
    }
  };

  useEffect(() => {
    fetchTerritoryContent();
    fetchMuniContent();
    fetchEdcContent();
  }, []);

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <AppAppBar
        onSelectView={selectView}
        onShowTileForm={() => setShowTileForm(true)}
        onShowScoreForm={() => setShowScoreForm(true)}
      />
      <Container
        maxWidth="lg"
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          mt: { xs: 'calc(14 * var(--template-spacing))', md: 16 },
          mb: { xs: 'calc(8 * var(--template-spacing))', md: 16 },
          gap: { xs: 'calc(2 * var(--template-spacing))', md: 4 },
        }}
      >
        {edcMode ? (
          <BattleForEdcContent
            onShowScoreForm={() => openScoreFormWith(null)}
            onPickSet={openScoreFormWith}
            territories={edcGoing}
          />
        ) : (
          <>
            {view === 'propose'         && <TeeTimeContent />}
            {view === 'scores'          && <ScoresContent />}
            {view === 'battle-for-muni' && <BattleForMuniContent onShowScoreForm={() => setShowScoreForm(true)} territories={muniHoles} parByHoleMuni={parByHoleMuni} timeline={muniTimeline} onRuleTap={handleRuleTap} />}
            {view === 'battle-for-em'   && <BattleForEmeraldContent  onShowScoreForm={() => setShowScoreForm(true)} territories={territories} parByHoleEm={parByHoleEm} timeline={emTimeline} onRuleTap={handleRuleTap} />}
          </>
        )}
      </Container>
      <Dialog
        className='score-form'
        open={showTileForm}
        onClose={() => setShowTileForm(false)}
      >
        <SubmitTileForm
          onSuccess={() => setShowTileForm(false)}
          onCancel={() => setShowTileForm(false)}
        />
      </Dialog>
      <Dialog
        className='score-form'
        open={showScoreForm}
        onClose={() => {
          setShowScoreForm(false);
          setPrefillSet(null);
        }}
      >
        <SubmitScoreForm
          prefill={prefillSet}
          onSuccess={async () => {
            setShowScoreForm(false);
            setPrefillSet(null);
            setShowThankYou(true);
            await new Promise((res) => setTimeout(res, 2100));
            await fetchTerritoryContent();
            await fetchMuniContent();
            if (edcMode) {
              await fetchEdcContent();
            }
          }}
          onCancel={() => {
            setShowScoreForm(false);
            setPrefillSet(null);
          }}
        />
      </Dialog>
      <Dialog className='ty-dialog' open={showThankYou} onClose={() => setShowThankYou(false)}>
        <Box sx={{ p: 4, minWidth: 300 }}>
          {!edcMode && (
            <Box sx={{display: 'flex', justifyContent: 'center'}}>
              <img
                src="https://images.ctfassets.net/zw84cuagwed8/657F89iQ3YSYAekJ9wtp5p/355a986267e9a9b92e1f4005f475ac27/g-med.png"
                alt="Battle for Muni"
                style={{
                  maxWidth: '32px',
                  height: 'auto',
                  display: 'block',
                  marginBottom: '10px',
                  marginTop: '-7px',
                  marginLeft: '0px'
                }}
              />
            </Box>
          )}
          <Typography variant="h3" gutterBottom sx={{textAlign: 'center', mb: 2, mt: 1}}>
            {edcMode ? "You're In!" : 'Score Submitted!'}
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center' }}>
            {edcMode
              ? "Locked in. See you on the rail — under the electric sky."
              : 'Your claim has been recorded. May the best golfer win.'}
          </Typography>
          <Button variant="contained" fullWidth onClick={() => setShowThankYou(false)}>
            Close
          </Button>
        </Box>
      </Dialog>
      <Dialog className='ty-dialog' open={showEmailThankYou} onClose={() => setShowEmailThankYou(false)}>
        <Box sx={{ p: 4, minWidth: 300 }}>
          {!edcMode && (
            <Box sx={{display: 'flex', justifyContent: 'center'}}>
              <img
                src="https://images.ctfassets.net/zw84cuagwed8/657F89iQ3YSYAekJ9wtp5p/355a986267e9a9b92e1f4005f475ac27/g-med.png"
                alt="Battle for Muni"
                style={{
                  maxWidth: '50px',
                  height: 'auto',
                  display: 'block',
                  marginBottom: '10px',
                  marginTop: '-7px',
                  marginLeft: '0px'
                }}
              />
            </Box>
          )}
          <Typography variant="h3" gutterBottom sx={{textAlign: 'center', mb: 2, mt: 1}}>
            Welcome!
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center' }}>
            By the time your finished wiping your clubs off, you will recieve an email with your battle key. Pepare for battle!
          </Typography>
          <Button variant="contained" fullWidth onClick={() => setShowEmailThankYou(false)}>
            Start Claiming
          </Button>
        </Box>
      </Dialog>
      <Dialog className='ty-dialog' onClose={() => setShowThankYou(false)}>
        <Box sx={{ p: 4, minWidth: 300 }}>
        {!edcMode && (
          <Box sx={{display: 'flex', justifyContent: 'center'}}>
            <img
              src="https://images.ctfassets.net/zw84cuagwed8/657F89iQ3YSYAekJ9wtp5p/355a986267e9a9b92e1f4005f475ac27/g-med.png"
              alt="Battle for Muni"
              style={{
                maxWidth: '32px',
                height: 'auto',
                display: 'block',
                marginBottom: '10px',
                marginTop: '-7px',
                marginLeft: '0px'
              }}
            />
          </Box>
        )}
          <Typography variant="h3" gutterBottom sx={{textAlign: 'center', mb: 2, mt: 1}}>
           Time Submitted!
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center' }}>
            Your time has been submitted.
          </Typography>
          <Button variant="contained" fullWidth onClick={() => setShowThankYou(false)}>
            Close
          </Button>
        </Box>
      </Dialog>
      <Footer onShowEmailThankYou={() => setShowEmailThankYou(true)} onShowScoreForm={() => setShowScoreForm(true)} onSelectView={selectView}/>
      <EdcBubbles onPick={(set) => openScoreFormWith(set)} />
      <EdcMeltOverlay />
      {edcMode && (
        <Box
          className="edc-exit-chip edc-bubble-chip"
          role="button"
          onClick={() => toggleBubbles()}
          title={bubblesEnabled ? 'Silence bubble suggestions' : 'Re-enable bubble suggestions'}
        >
          {bubblesEnabled ? 'BUBBLES · ON' : 'BUBBLES · OFF'}
        </Box>
      )}
      {edcMode && (
        <Box
          className="edc-exit-chip"
          role="button"
          onClick={() => toggleEdc()}
          title="Exit EDC mode"
        >
          EDC MODE · EXIT
        </Box>
      )}
      <Box
        className={edcMode ? "edc-bottom-nav" : "bottom-nav"}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: showBottomNav && !edcMode ? 'translateX(-50%) translateY(-30%)' : showBottomNav && edcMode ? 'translateX(-50%) translateY(-70%)' : 'translateX(-50%) translateY(100%)',
          width: '92%',
          display: { xs: 'flex', sm: 'none' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: edcMode ? 1 : 0,
          px: edcMode ? 1.5 : 2,
          py: 1,
          borderRadius: 2,
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '5px solid #106a41',
          backgroundColor: 'rgba(255, 255, 255, 0.4)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          transition: 'transform 0.3s ease-in-out',
          zIndex: 1300,
          minHeight: 48,
        }}
      >
        {edcMode ? (
          <>
            <Button
              color="primary"
              variant="contained"
              size="small"
              onClick={() => openScoreFormWith(null)}
              sx={{ flex: 1 }}
            >
              I'm Going
            </Button>
            <IconButton
              onClick={() => toggleEdc()}
              title="Exit EDC mode"
              sx={{
                color: '#ffd400 !important',
                border: '1px solid #ffd400',
                borderRadius: 999,
                px: 1.5,
                flexShrink: 0,
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </>
        ) : (
          <>
            <IconButton onClick={() => selectView('battle-for-em')}>
              <Box component="img" src="https://images.ctfassets.net/zw84cuagwed8/3K6HzOpyJuxcb3LbXa4yV0/aa8d0a800e1756fcca5fe4faefa0654f/cloverI.png" alt="Emerald" sx={{ width: 24, height: 24 }} />
            </IconButton>
            <IconButton onClick={() => selectView('battle-for-muni')}>
              <Box component="img" src="https://images.ctfassets.net/zw84cuagwed8/k8gWs7EEfBEwSA7vIeUGR/4fbf46a762cc646b78d3b570d2790382/muniI.png" alt="Muni" sx={{ width: 24, height: 24 }} />
            </IconButton>
            <IconButton onClick={() => setShowTileForm(false)}>
              <Box component="img" src="https://images.ctfassets.net/zw84cuagwed8/lngZAa6G7lexpjCOy5SYx/162581c7f863bea9163781a1b60aaec5/time_.png" alt="Claim" sx={{ width: 24, height: 24 }} />
            </IconButton>
            <IconButton onClick={() => setShowScoreForm(true)}>
              <Box component="img" src="https://images.ctfassets.net/zw84cuagwed8/2bKZDoCC3jp4ynax5bGFVL/45409b47ff7db9ad73e7514085e211ec/flagI.png" alt="Add Score" sx={{ width: 24, height: 24 }} />
            </IconButton>
          </>
        )}
      </Box>

    </AppTheme>
  );
}

export default function Blog(props) {
  return (
    <EdcProvider>
      <BlogInner {...props} />
    </EdcProvider>
  );
}
